import React from 'react';
import { Editor } from 'slate';
import { v4 as uuid4 } from 'uuid';
import { AulaPlugin } from '../plugins';
import { isCurrentSelectionCollapsed } from '../../lib/queries';
import { insertVoidElement } from '../../lib/commands';
import {
  BibliUEntity,
  bibliUType,
  isBibliUElement,
  isBibliUUrl,
} from './bibliUType';
import { getValidUrl } from '../../utils/getValidUrl';
import { BibliULink } from '../../components/BibliULink/BibliULink';
import { AulaEditorContext } from '../../types';

const getFullUrl = ({ text, editor }: { text: string; editor: Editor }) =>
  isCurrentSelectionCollapsed(editor) && getValidUrl(text);

export const insertBibliULink = (
  url: string,
  editor: Editor,
  context?: Omit<AulaEditorContext, 'entities'>,
) => {
  if (context == null) {
    throw new Error('context undefined');
  }

  const entity = context.createEntity({
    type: 'lti',
    url: decodeURIComponent(url),
    /** An LTI block is accepted to be in the progress of loading data,
     *  if its data field is empty. Currently, BibliU LTI blocks don't
     *  receive message after content selection so a random link id is
     *  explicitly set immediately after adding the block to the document.
     */
    data: { linkId: uuid4() },
  } as BibliUEntity);

  insertVoidElement(editor, {
    type: bibliUType,
    entity,
  });
};

export const bibliu: AulaPlugin = (editableProps, editor, context) => {
  const { insertText, isVoid } = editor;

  editor.isVoid = (element) => {
    if (element.type === bibliUType) return true;
    return isVoid(element);
  };

  // Handles case where url was copied from outside of editor e.g. from the address bar
  editor.insertText = (text: string) => {
    if (text.length > 1) {
      const elements = text.split(' ');
      elements.forEach((element, index) => {
        const fullUrl = getFullUrl({ editor, text: element });
        if (fullUrl && isBibliUUrl(fullUrl)) {
          insertBibliULink(fullUrl, editor, context);
        } else {
          insertText(element);
          if (index !== elements.length - 1 && !fullUrl) insertText(' ');
        }
      });
    } else {
      insertText(text);
    }
  };

  return {
    ...editableProps,
    renderElement: (props) => {
      const { children, element, attributes } = props;

      if (isBibliUElement(element)) {
        return (
          <div {...attributes}>
            <BibliULink element={element} />
            {children}
          </div>
        );
      }

      return editableProps.renderElement?.(props) || <p />;
    },
  };
};
